import { RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';

import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Order } from '~hooks/useOrders/models';
import { getFormattedRateValue } from '~pages/Settings/RatesManagement/rateUtils';
import { useStores } from '~store';
import {
  extractRateInfoFromDefaultSiteRateSchedule,
  extractRateInfoFromOrderOrProject,
  userCompanyOwnsOrder,
} from '~utils/orders';

export default function useOrderCustomerRate(order: Order) {
  const { currencyFormatter } = useCompanyCurrency();
  const { userStore } = useStores();

  // Applying a rate card to an order is optional, and a project with a single card usually
  // leaves it unapplied — so an absent card means "not chosen", not "no rate". The order's
  // own rate, which the backend has already resolved, is the answer in that case. That
  // holds only while one card is in play: with several unapplied cards none is
  // authoritative, and showing one rate would read as "the" rate for the project when it
  // is one candidate of many, so the rate is withheld until a card is applied.
  const appliedRateCardCustomerRate = extractRateInfoFromDefaultSiteRateSchedule(
    order,
    'customer',
  );
  const orderHasAppliedRateCard = Boolean(order.defaultSiteRateSchedule);
  const orderHasMultipleApplicableRateCards = (order.applicableRateCardCount ?? 0) > 1;
  const isCustomerRateAmbiguous =
    !orderHasAppliedRateCard && orderHasMultipleApplicableRateCards;
  const customerRate = isCustomerRateAmbiguous
    ? null
    : (appliedRateCardCustomerRate ??
      extractRateInfoFromOrderOrProject(order, 'customer'));
  // Rate cards live with the order-owning company, so on a shared (connected-company)
  // order the acting company's rate-card fetch is unauthorized and fails every time —
  // don't offer a hover that can only dead-end. The explicit owner-id check keeps an
  // owner-less order (or a not-yet-hydrated user company) from slipping through as
  // "owned" when both sides are undefined.
  const orderHasKnownOwner = Boolean(order.company?.id);
  // A cross-tenant reader is authorized beyond its own company, so a count can arrive on
  // an order this company does not own and whose rate cards it still cannot fetch.
  const userCompanyOwnsThisOrder =
    orderHasKnownOwner && userCompanyOwnsOrder(userStore, order);
  // "Rates Set" is a factual claim, so it only appears once the payload says cards exist.
  // A withheld count reads the same as zero: the affordance would only dead-end.
  const orderHasApplicableRateCards = (order.applicableRateCardCount ?? 0) > 0;
  const shouldShowRateCardIndicator =
    userCompanyOwnsThisOrder && orderHasApplicableRateCards;

  if (!customerRate) {
    return { customerRateText: null, shouldShowRateCardIndicator };
  }

  const formattedRateValue = getFormattedRateValue(
    customerRate.rateType as RateType,
    customerRate.rateValue,
    currencyFormatter,
    true,
  );
  const rateTypeLabel = t(`rate_types.${snakeCase(customerRate.rateType)}`);

  return {
    customerRateText: formattedRateValue
      ? `${formattedRateValue} ${rateTypeLabel}`
      : rateTypeLabel,
    shouldShowRateCardIndicator,
  };
}
